import { message, Spin, Checkbox, Typography, Form } from 'antd';
import { Banner } from 'components/ui/Banner';
import { useCallback, useEffect, useState } from 'react';
import { familyService } from 'services';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import { useAsync } from 'react-async-hook';
import PageMeta from 'components/PageMeta/PageMeta';
import RadioCards from 'components/RadioCards/RadioCards';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { getApplicantFirstName, getBranchNameById } from '../../utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export function FamilyApplicationCivilianEmployment({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);

  // Form state
  const [form] = Form.useForm();

  const [submitting, setSubmitting] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [paymentSystemUnknown, setPaymentSystemUnknown] = useState(false);

  const isCompletedBySponsor = application?.completedBySponsor === true;

  // API fetch for payment systems
  const { result: paymentSystemOptions = null } = useAsync(async () => {
    return familyService.getOptions('/ref-data/payment-systems', {
      cache: { interpretHeader: false },
    });
  }, []);

  const onSubmit = useCallback(async () => {
    const _goToNextStep = (_newApplication) => {
      // Use getNextStep to determine the next step
      const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.CIVILIAN_EMPLOYMENT, _newApplication, stepConfig);
      setStep({
        applicationId: _newApplication?.id,
        newStep: nextStep,
      });
    };

    if (readOnly) {
      _goToNextStep(application);
      return;
    }

    try {
      setSubmitting(true);

      // Note: paymentSystem doesn't trigger cascading cleaning as it's typically a leaf field
      const updatedMilitaryDetails = {
        ...application?.applicant?.militaryDetails,
        paymentSystem: paymentSystemUnknown
          ? null
          : {
              id: selectedOption,
            },
        paymentSystemUnknown,
      };

      const _newSponsorData = await familyService.addUpdateAdult(household?.id, application?.applicant?.id ?? null, {
        ...application?.applicant,
        militaryDetails: updatedMilitaryDetails,
      });

      const _newApplication = await familyService.saveApplication({
        ...application,
        applicant: {
          ...application?.applicant,
          ..._newSponsorData,
        },
      });

      setApplication(_newApplication);
      _goToNextStep(_newApplication);
    } catch (error) {
      message.error('Something went wrong');
      newrelic.noticeError(error);
    } finally {
      setSubmitting(false);
    }
  }, [application, household?.id, readOnly, selectedOption, paymentSystemUnknown, setApplication, setStep, stepConfig]);

  // Set the selected option based on the application data
  useEffect(() => {
    if (application?.applicant?.militaryDetails?.paymentSystem?.id !== undefined && paymentSystemOptions !== null) {
      setSelectedOption(application?.applicant?.militaryDetails?.paymentSystem?.id);
    }
  }, [application?.applicant?.militaryDetails?.paymentSystem?.id, paymentSystemOptions]);

  // Set the paymentSystemUnknown state based on the application data
  useEffect(() => {
    if (application?.applicant?.militaryDetails?.paymentSystemUnknown !== undefined) {
      setPaymentSystemUnknown(application?.applicant?.militaryDetails?.paymentSystemUnknown);
    }
  }, [application?.applicant?.militaryDetails?.paymentSystemUnknown]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={paymentSystemOptions === null}>
      <PageMeta title="Civilian Employment" />

      <Form form={form} onFinish={onSubmit} layout="vertical">
        <div className="p-7 pt-0">
          <Banner
            question={
              isCompletedBySponsor
                ? `What type of Department of ${getBranchNameById(
                    application?.applicant?.militaryDetails?.branch?.id,
                  )} civilian employee are you?`
                : `What type of Department of ${getBranchNameById(
                    application?.applicant?.militaryDetails?.branch?.id,
                  )} civilian employee are is ${getApplicantFirstName(application?.applicant)}?`
            }
            className="my-12"
          />

          <Form.Item
            name="rank"
            rules={[
              {
                validator: (_, value) => {
                  if (!selectedOption && !paymentSystemUnknown) {
                    return Promise.reject(new Error('Please select an option, or check the "I don\'t know" box'));
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <RadioCards
              variant="standard"
              options={paymentSystemOptions?.map((_option) => ({
                label: _option.name,
                value: _option.id,
              }))}
              value={selectedOption}
              onChange={(value) => {
                setSelectedOption(value);
              }}
              disabled={readOnly || paymentSystemUnknown}
            />

            <Checkbox
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedOption(null);
                }

                setPaymentSystemUnknown(e.target.checked);
              }}
              checked={paymentSystemUnknown}
              disabled={readOnly}
              className="mt-10"
            >
              <Typography.Text className="text-[15px] font-semibold">I don't know right now</Typography.Text>
            </Checkbox>
          </Form.Item>
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            disabled: submitting,
            onClick: () => {
              // Use getPreviousStep to determine the previous step
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.CIVILIAN_EMPLOYMENT,
                application,
                stepConfig,
              );

              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
          }}
          nextButtonProps={{
            loading: submitting,
          }}
        />
      </Form>
    </Spin>
  );
}
